import { useEffect, useRef, useState } from "react";
import { Flashlight as Torch, Zap, RotateCcw, AlertOctagon } from "lucide-react";

const COLORS = [
  { name: "White", value: "#FFFFFF" },
  { name: "Cyan", value: "#00F0FF" },
  { name: "Green", value: "#39FF14" },
  { name: "Yellow", value: "#FFE300" },
  { name: "Red", value: "#FF003C" },
  { name: "Warm", value: "#FFD08A" },
];

// SOS morse timing (ms), on/off pattern
const SOS = [200, 200, 200, 200, 200, 600, 600, 200, 600, 200, 600, 600, 200, 200, 200, 200, 200, 1400];

export default function JixxeoFlashlight() {
  const [on, setOn] = useState(true);
  const [brightness, setBrightness] = useState(100);
  const [color, setColor] = useState(COLORS[0].value);
  const [strobe, setStrobe] = useState(false);
  const [sos, setSos] = useState(false);
  const [flash, setFlash] = useState(true);
  const timerRef = useRef(null);
  const sosIdxRef = useRef(0);
  const wakeLockRef = useRef(null);

  // Wake lock so screen doesn't dim while flashlight is on
  useEffect(() => {
    (async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
        }
      } catch {}
    })();
    return () => {
      try { wakeLockRef.current?.release(); } catch {}
    };
  }, []);

  // Strobe
  useEffect(() => {
    if (!strobe || sos) return;
    timerRef.current = setInterval(() => setFlash((f) => !f), 90);
    return () => clearInterval(timerRef.current);
  }, [strobe, sos]);

  // SOS morse
  useEffect(() => {
    if (!sos) { setFlash(true); return; }
    let cancelled = false;
    const step = () => {
      if (cancelled) return;
      const dur = SOS[sosIdxRef.current % SOS.length];
      const isOn = sosIdxRef.current % 2 === 0;
      setFlash(isOn);
      sosIdxRef.current++;
      timerRef.current = setTimeout(step, dur);
    };
    step();
    return () => { cancelled = true; clearTimeout(timerRef.current); setFlash(true); };
  }, [sos]);

  const lit = on && flash;
  const bg = lit ? color : "#050505";

  return (
    <div className="min-h-screen relative select-none overflow-hidden"
         style={{ background: bg, opacity: lit ? brightness / 100 : 1, transition: strobe || sos ? "none" : "background 0.15s, opacity 0.2s" }}>
      {/* Controls overlay */}
      <div className="absolute inset-x-0 top-4 flex justify-center z-10 pointer-events-none">
        <div className={`glass-strong rounded-full px-4 py-2 mono text-[10px] uppercase tracking-widest flex items-center gap-2 pointer-events-auto ${lit ? "text-black bg-white/80" : "text-white"}`}>
          <Zap className="w-3 h-3" /> Jixxeo Flashlight
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 z-10 p-4 md:p-6">
        <div className="glass-strong rounded-3xl p-4 md:p-6 border border-white/10 max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setOn((v) => !v)}
              className="flex-1 py-3 rounded-full font-heading font-black uppercase tracking-tighter text-lg flex items-center justify-center gap-2"
              style={{ background: on ? "#FFE300" : "rgba(255,255,255,0.06)", color: on ? "#000" : "#fff" }}>
              <Torch className="w-5 h-5" /> {on ? "ON" : "OFF"}
            </button>
            <button onClick={() => setStrobe((s) => !s)}
              className={`px-4 py-3 rounded-full mono text-[10px] uppercase tracking-widest font-bold ${strobe ? "bg-[#FF003C] text-black" : "border border-white/15 text-white/80"}`}>
              Strobe
            </button>
            <button onClick={() => setSos((s) => { sosIdxRef.current = 0; return !s; })}
              className={`px-4 py-3 rounded-full mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 ${sos ? "bg-[#FF003C] text-black" : "border border-white/15 text-white/80"}`}>
              <AlertOctagon className="w-3 h-3" /> SOS
            </button>
          </div>

          <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Brightness</div>
          <input type="range" min="10" max="100" value={brightness} onChange={(e) => setBrightness(+e.target.value)}
            className="w-full accent-[#FFE300]" />

          <div className="mono text-[10px] uppercase tracking-widest text-white/50 mt-4 mb-2">Color</div>
          <div className="grid grid-cols-6 gap-2">
            {COLORS.map((c) => (
              <button key={c.value} onClick={() => setColor(c.value)}
                className="aspect-square rounded-xl border-2 transition-transform hover:-translate-y-0.5"
                style={{ background: c.value, borderColor: color === c.value ? "#00F0FF" : "rgba(255,255,255,0.1)" }}
                title={c.name} />
            ))}
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-white/40 mt-4 text-center">
            no install · runs in browser · jixxeo apps
          </div>
        </div>
      </div>
    </div>
  );
}
